/**
 * RECENTLY OPENED — the personal recall list, on the governed personal store.
 *
 * #2325 ruling 3, and its one hard constraint: *"Do not add a second
 * local-storage list beside an existing governed personal-preference
 * mechanism."* There already was one. `core.user_setting` — read by
 * `core.my_settings()`, written by `core.set_user_setting()`, both SECURITY
 * INVOKER and both scoped `user_id = (select auth.uid())` — carries the pins,
 * the column widths, the split width and the relationship-picker frequency.
 * Recents were the one personal collection sitting outside it, in
 * `localStorage['solum.recents']`.
 *
 * ── what that cost, measured rather than argued ────────────────────────────
 * The key was ONE key for the browser. Two people signing into the same console
 * on the same machine — a shared desk, a demo laptop, a support session — saw
 * each other's history, and the second one's first act was to read a list of
 * records the first had opened. Nothing was disclosed that RLS would not also
 * disclose on click (the names are resolved through `core.titles_of`, which
 * refuses what the caller cannot see, so an unreachable row renders nothing) —
 * but "which records did my colleague open this morning" is itself a fact, and
 * nobody decided to publish it. On the governed store the question cannot
 * arise: the row is keyed by the caller's own principal inside the door.
 *
 * The device-local argument that used to be written in `pins.ts` — *"a
 * machine-local trace of where you have been is exactly what that is for"* —
 * is superseded by that ruling. It was a reasonable reading and it is the wrong
 * one for a product where the same browser serves more than one person.
 *
 * ── the shape, and why it is not simply a hook ─────────────────────────────
 * `getAllRecents()` and `getRecents()` are called SYNCHRONOUSLY from ⌘K's
 * `useMemo` and from the relationship picker's `search()`, neither of which can
 * await a round trip and neither of which should grow a query of its own for a
 * list the shell already holds. So this module keeps an in-memory cache and a
 * tiny external store, and ONE mounted component (`useRecentsStore`, in
 * `AppShell`) owns the governed read, the debounced write and the hydration.
 * Every existing caller keeps its shape; what changed is where the bytes live.
 *
 * ── never write over an unread list ────────────────────────────────────────
 * `pins.ts` states the rule and it binds harder here, because recents are
 * written by NAVIGATION rather than by a click: an unhydrated flush would post
 * a one-entry list over a settled history on the first record somebody opened.
 * So a visit recorded before the read settles goes to `pending`, hydration
 * MERGES it in front of the server's list, and only then does anything flush.
 *
 * The in-memory cache is deliberately not persisted anywhere on the device. It
 * dies with the tab, which is the correct lifetime for a mirror of a
 * server-side row, and it is the other half of the two-users property: there is
 * no local artefact for a second principal to inherit.
 */
import { useCallback, useEffect, useRef, useSyncExternalStore } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { currentPrincipalId } from '@solum/auth'
import { useSolum } from '../data/client'

/** The key inside the caller's `core.user_setting` map. Beside `pins`. */
const KEY = 'recents'

/**
 * The governed maximum (#2325 §2, "keep a finite governed maximum").
 *
 * ONE clamp over the whole history, not the old ten-per-entity — which was not
 * a maximum at all: the number of entity buckets was unbounded, so a person
 * working across thirty modules carried three hundred entries and posted them
 * on every navigation. Forty is the drawer's twelve rows and ⌘K's per-entity
 * slices with room to spare, and it bounds the row this writes.
 */
export const MAX_RECENTS = 40

/** Where a person has been. `at` is an ISO instant, so it sorts as a string. */
export interface RecentItem { entity: string; id: string; at: string }

/* ── the module store ─────────────────────────────────────────────────────── */

let cache: RecentItem[] = []
/** Visits recorded before the governed read settled. Merged, never posted alone. */
let pending: RecentItem[] = []
let hydrated = false
let principal: string | null = null
let version = 0

const subs = new Set<() => void>()
const notify = () => { version += 1; subs.forEach((fn) => fn()) }

let writer: ((next: RecentItem[]) => void) | null = null
let flushQueued = false

function clamp(items: RecentItem[]): RecentItem[] {
  const seen = new Set<string>()
  const out: RecentItem[] = []
  for (const item of items) {
    const key = `${item.entity}:${item.id}`
    if (seen.has(key)) continue      // first occurrence wins — the list is newest-first
    seen.add(key)
    out.push(item)
    if (out.length === MAX_RECENTS) break
  }
  return out
}

/**
 * A MICROTASK, not a timer — and that is a durability decision, not a nicety.
 *
 * This was a 400 ms debounce, and the browser suite found what that costs: open
 * a record, then load another page, and the visit is gone. The old device-local
 * store was a synchronous `localStorage.setItem` and could not lose a write; a
 * governed round trip can, and a timer widens the window to the point where an
 * ordinary navigation falls inside it. Two of the new `accounts.spec.ts` cases
 * failed on exactly that, reading as "the history is broken" — which it was.
 *
 * A microtask still coalesces the only burst that exists (several
 * `recordRecent` calls in one React commit) and runs before the browser can
 * process the navigation that would have eaten it.
 *
 * ONE guard for "not before the read settled", and it is at the CALL SITE.
 * This used to re-check `hydrated` inside the timer as well; mutation M6 —
 * deleting that second check — came back GREEN, because nothing schedules a
 * flush before hydration and the inner check was unreachable. Two guards for
 * one property means one is decoration, and a decoration is worse than nothing
 * because it reads as protection.
 */
function scheduleFlush() {
  if (flushQueued) return
  flushQueued = true
  queueMicrotask(() => {
    flushQueued = false
    /* Reads `cache` AT FIRE TIME, not at schedule time: more visits may have
       landed in the same commit. */
    writer?.(cache)
  })
}

/** Parse whatever the settings map holds under `recents`, refusing anything else. */
export function readStoredRecents(settings: unknown): RecentItem[] {
  const raw = (settings as Record<string, unknown> | null | undefined)?.[KEY]
  if (!Array.isArray(raw)) return []
  const items: RecentItem[] = []
  for (const row of raw) {
    if (!row || typeof row !== 'object') continue
    const { entity, id, at } = row as Record<string, unknown>
    if (typeof entity !== 'string' || !entity) continue
    if (typeof id !== 'string' || !id) continue
    items.push({ entity, id, at: typeof at === 'string' ? at : '1970-01-01T00:00:00Z' })
  }
  return clamp([...items].sort((a, b) => b.at.localeCompare(a.at)))
}

/**
 * The governed read has settled for `who`: the cache is REBUILT from what came
 * back, never merged into.
 *
 * That rebuild is the two-users property, and saying so is the point. This
 * function first carried an explicit `if (a different principal) cache = []`,
 * and mutation M5 — deleting it — came back GREEN: the very next line
 * overwrites `cache` wholesale, so the clear had never done anything. A guard
 * a mutation cannot reach is a guard nobody is relying on, whatever it looks
 * like. What IS load-bearing is `carried`, below.
 *
 * `carried` is the visits recorded while the read was in flight, and it applies
 * on the FIRST hydration only. `principal === null` means "nobody has hydrated
 * yet", not "a different person" — on a cold load the only session that can
 * have recorded anything is the one now settling, and the first run of
 * `recents.test.ts` caught the version that conflated the two and threw those
 * visits away. A LATER hydration for a different principal carries nothing, so
 * one person's visit can never land in another's list.
 */
export function hydrateRecents(stored: RecentItem[], who: string | null) {
  if (hydrated && principal === who) return
  const carried = principal === null ? pending : []
  principal = who
  cache = clamp([...carried, ...stored])
  pending = []
  hydrated = true
  if (carried.length > 0) scheduleFlush()
  notify()
}

/** Register the governed writer. Returns an unsubscribe, for the mounting effect. */
export function registerRecentsWriter(fn: (next: RecentItem[]) => void): () => void {
  writer = fn
  return () => { if (writer === fn) writer = null }
}

/**
 * A record was OPENED. Deduplicated by `(entity, id)` and moved to the top, so
 * reopening the oldest promotes it rather than adding a second row.
 *
 * Callers must only reach here for a record route that actually RESOLVED
 * (#2325 §2) — `RecordBody` is the one place every full open passes, and it
 * waits for its row read before calling this.
 */
export function recordRecent(entity: string, id: string) {
  if (!entity || !id) return
  const entry: RecentItem = { entity, id, at: new Date().toISOString() }
  cache = clamp([entry, ...cache])
  if (hydrated) scheduleFlush()
  else pending = clamp([entry, ...pending])
  notify()
}

/**
 * FORGETTING — `removeRecent`, `clearRecents`, and the undo they owe (#2350 §9).
 *
 * The panel's own change log names the gap: *"Added row-level remove-from-
 * recent, and 'Clear recents' in the footer. The original offered no way to
 * remove anything."* A recall list you cannot edit is a list that slowly stops
 * being about you — and on a shared screen it is worse than that, because the
 * only way to take a record off it was to open forty others.
 *
 * ── the three rules these follow, and why each one is load-bearing ──────────
 *
 * 1 · **REFUSED BEFORE HYDRATION.** `pins.ts` states the rule for pins and it
 *     binds harder here for removal than it does for a visit. A visit posted
 *     over an unread list loses history; a REMOVAL posted over an unread list
 *     writes a list that was computed from nothing, which is `[]` — it would
 *     wipe the whole history on the first click of a cold load. Both return a
 *     falsy answer rather than throwing, because the caller is a button and the
 *     honest response to "not ready" is a disabled control, not an error.
 *
 * 2 · **ONE STORE, so every consumer moves at once.** `notify()` is what makes
 *     that true: ⌘K reads `getAllRecents()` inside a `useMemo` keyed on
 *     `useRecentsVersion()`, and the relationship picker calls `getRecents`
 *     synchronously. Neither holds a copy, so neither can drift — which is the
 *     design's *"Clearing/removing in the panel updates those consumers
 *     immediately. Do not add panel-only arrays or a second query key."*
 *
 * 3 · **UNDO IS A MERGE, NOT A SNAPSHOT RESTORE.** `clearRecents` hands back
 *     what it removed and `restoreRecents` puts it BACK IN FRONT of whatever
 *     the list holds now, deduplicated by the same `(entity, id)` rule. A
 *     restore that assigned the old array wholesale would silently discard any
 *     record opened between the clear and the undo — and the window between
 *     them is exactly a few seconds of somebody reading a toast, which is
 *     precisely when a navigation happens.
 *
 * Removal touches RECENTS ONLY. Navigation pinning belongs to the navigation
 * rail and its existing pins store; the history panel neither reads nor writes
 * it. A person who clears history has not asked to rearrange their workspace.
 */

/**
 * Forget ONE record. Returns whether anything was removed, so a caller can tell
 * "gone" from "was not there" without reading the list back.
 */
export function removeRecent(entity: string, id: string): boolean {
  if (!hydrated) return false
  if (!entity || !id) return false
  const next = cache.filter((item) => !(item.entity === entity && item.id === id))
  if (next.length === cache.length) return false
  cache = next
  scheduleFlush()
  notify()
  return true
}

/**
 * Forget everything. Returns what was removed — the caller's undo, and the only
 * copy of it: nothing here retains a shadow list, because a store that quietly
 * kept the history it was told to delete would be the defect this fixes wearing
 * a different hat.
 */
export function clearRecents(): RecentItem[] {
  if (!hydrated) return []
  if (cache.length === 0) return []
  const removed = cache
  cache = []
  scheduleFlush()
  notify()
  return removed
}

/**
 * Put removed entries back, newest-first and deduplicated against what is there
 * now. A no-op on an empty list, so an undo offered after the list already
 * recovered cannot write anything.
 */
export function restoreRecents(items: RecentItem[]): boolean {
  if (!hydrated) return false
  if (!Array.isArray(items) || items.length === 0) return false
  const merged = clamp([...items, ...cache].sort((a, b) => b.at.localeCompare(a.at)))
  if (merged.length === cache.length
      && merged.every((item, i) => item.entity === cache[i]!.entity && item.id === cache[i]!.id)) {
    return false
  }
  cache = merged
  scheduleFlush()
  notify()
  return true
}

/** Whether the governed read has settled — a removal control is disabled until it has. */
export function recentsReady(): boolean {
  return hydrated
}

/** Everything, newest first. The drawer's Recently opened and ⌘K read this. */
export function getAllRecents(): RecentItem[] {
  return cache
}

/** One entity's recent ids, newest first — the relationship picker's fuel. */
export function getRecents(entity: string): string[] {
  return cache.filter((item) => item.entity === entity).map((item) => item.id)
}

/** Test seam. Nothing in the app calls this. */
export function resetRecentsStore() {
  cache = []; pending = []; hydrated = false; principal = null; writer = null
  flushQueued = false
  notify()
}

/** Re-render when a visit or a hydration changes the list. */
export function useRecentsVersion(): number {
  return useSyncExternalStore(
    useCallback((cb: () => void) => { subs.add(cb); return () => { subs.delete(cb) } }, []),
    () => version,
    () => version,
  )
}

/**
 * The one mounted owner: reads `my_settings`, hydrates, and flushes.
 *
 * Mounted in `AppShell`, once, beside `CrmSettingsSync` — for the same reason
 * that component exists. The read shares `['user-settings']` with `usePins`, so
 * the pair costs one round trip rather than two, and the write merges into that
 * cache rather than invalidating it: a refetch per navigation would turn a
 * recall list into a traffic source.
 */
export function useRecentsStore() {
  const { client } = useSolum()
  const qc = useQueryClient()
  const who = currentPrincipalId() ?? null

  const settings = useQuery({
    queryKey: ['user-settings'],
    queryFn: () => client.raw().rpcScalar<Record<string, unknown>>('my_settings'),
    staleTime: 60_000,
  })

  const save = useMutation({
    mutationFn: (next: RecentItem[]) =>
      client.raw().rpcScalar('set_user_setting', { p_key: KEY, p_value: next }),
  })

  /* The live writer, behind a ref so registration happens once. Re-registering
     on every render of a mutation object would tear the writer down between a
     scheduled flush and its firing. */
  const latest = useRef<(next: RecentItem[]) => void>(() => {})
  latest.current = (next: RecentItem[]) => {
    if (!settings.isSuccess) return          // never write over an unread map
    save.mutate(next)
    qc.setQueryData(['user-settings'], (prev: Record<string, unknown> | undefined) =>
      ({ ...(prev ?? {}), [KEY]: next }))
  }
  useEffect(() => registerRecentsWriter((next) => latest.current(next)), [])

  useEffect(() => {
    if (!settings.isSuccess) return
    hydrateRecents(readStoredRecents(settings.data), who)
  }, [settings.isSuccess, settings.data, who])

  /* The pre-#2325 device list. Removed rather than migrated, deliberately: it
     is not attributable to a principal, so importing it is precisely the
     cross-user leak this change closes. Leaving it would be worse still — a
     dormant copy of somebody's history with nothing left that reads it. */
  useEffect(() => {
    try { localStorage.removeItem('solum.recents'); localStorage.removeItem('solum.pins') }
    catch { /* private mode */ }
  }, [])

  /* A refused settings read is SETTLED, not pending: the drawer's empty state
     distinguishes "nothing here" from "cannot be shown" and needs to know. */
  return { ready: settings.isSuccess, failed: settings.isError }
}
